import * as THREE from 'three';
import { GameManager } from './GameManager';
import { EnemyManager } from './enemies/EnemyManager';
import { WaveManager, WaveAnnouncement } from './waves/WaveManager';
import { PlayerController } from './player/PlayerController';
import { GameStateEnum, setGameState } from '@/store/gameStateSlice';
import { store } from '@/store/store';
import { resetWaveState } from '@/store/waveSlice';
import { updatePlayerHealth, updatePlayerScore, resetPlayerState, updatePlayerResources } from '@/store/playerSlice';
import { BowController } from './bow/BowController';
import { setWaveAnnouncement } from '@/store/announcementSlice';

/**
 * Controls the overall game flow and integrates all game systems
 */
export class GameController {
  private gameManager: GameManager;
  private enemyManager: EnemyManager;
  private waveManager: WaveManager;
  private scene: THREE.Scene;
  private playerController: PlayerController | null = null;
  private isGameActive: boolean = false;
  private scorePerEnemy: number = 100;
  private scorePerWave: number = 1000;
  private currentScore: number = 0;
  private goldPerWave: number = 100;
  
  /**
   * Creates a new GameController
   * @param gameManager The game manager
   */
  constructor(gameManager: GameManager) {
    this.gameManager = gameManager;
    this.scene = gameManager.getThreeJSManager().getScene();
    
    // Create enemy manager
    this.enemyManager = new EnemyManager(this.scene);
    
    // Set camera for healthbar facing
    const camera = gameManager.getThreeJSManager().getCamera();
    this.enemyManager.setCamera(camera);
    
    // Create wave manager
    this.waveManager = new WaveManager(this.enemyManager);
    
    // Set up wave callbacks
    this.setupWaveCallbacks();
  }
  
  /**
   * Sets up callbacks for wave events
   */
  private setupWaveCallbacks(): void {
    // Wave start callback
    this.waveManager.setOnWaveStart((waveNumber) => {
      console.log(`Wave ${waveNumber} started`);
      
      // Play wave start sound/effect
      // this.playSound('wave_start');
    });
    
    // Wave complete callback
    this.waveManager.setOnWaveComplete((waveNumber) => {
      console.log(`Wave ${waveNumber} completed`);
      
      // Award points for completing the wave
      this.addScore(this.scorePerWave * waveNumber);
      
      // Award gold for completing the wave
      this.addGold(this.goldPerWave * waveNumber);
      
      // Set game state to WAVE_COMPLETED to show upgrade menu
      store.dispatch(setGameState(GameStateEnum.WAVE_COMPLETED));
      
      // Play wave complete sound/effect
      // this.playSound('wave_complete');
    });
    
    // All waves complete callback
    this.waveManager.setOnAllWavesComplete(() => {
      console.log('All waves completed!');
      
      // End the game with victory
      this.endGame(true);
      
      // Play victory sound/effect
      // this.playSound('victory');
    });
    
    // Wave announcement callback
    this.waveManager.setOnWaveAnnouncement((announcement: WaveAnnouncement) => {
      console.log(`Wave announcement: ${announcement.message}`);
      
      // Dispatch the announcement to Redux
      store.dispatch(setWaveAnnouncement(announcement));
      
      // Play announcement sound/effect based on wave type
      // this.playSound(`wave_${announcement.waveType.toLowerCase()}`);
    });
  }
  
  /**
   * Gets the score multiplier for an enemy type
   * @param enemyType The enemy type
   * @returns The score multiplier
   */
  private getEnemyTypeScoreMultiplier(enemyType: string): number {
    switch (enemyType) {
      case 'GRUNT':
        return 1.0;
      case 'ARCHER':
        return 1.5;
      case 'BRUTE':
        return 2.0;
      case 'BOSS':
        return 5.0;
      default:
        return 1.0;
    }
  }
  
  /**
   * Adds points to the score
   * @param points The points to add
   */
  private addScore(points: number): void {
    this.currentScore += points;
    
    // Update Redux store
    store.dispatch(updatePlayerScore(this.currentScore));
  }
  
  /**
   * Adds gold to the player
   * @param amount The amount of gold to add
   */
  private addGold(amount: number): void {
    const currentGold = store.getState().player.resources.gold;
    store.dispatch(updatePlayerResources({
      gold: currentGold + amount
    }));
  }
  
  /**
   * Starts a new game
   */
  public startGame(): void {
    if (this.isGameActive) return;
    
    console.log('Starting new game');
    
    // Reset game state
    this.resetGameState();
    
    // Set game as active
    this.isGameActive = true;
    
    // Clear enemy callbacks before setting up new ones
    this.enemyManager.clearEnemyDefeatedCallbacks();
    
    // Get player controller
    this.playerController = this.gameManager.getPlayerController();
    
    // Connect bow controller with enemy manager for arrow collision detection
    if (this.playerController) {
      const bowController = this.playerController.getBowController();
      if (bowController) {
        bowController.setEnemyManager(this.enemyManager);
        // Set the player controller reference for velocity inheritance
        bowController.setPlayerController(this.playerController);
      }
    }
    
    // Start wave progression
    this.waveManager.startWaves();
    
    // Set up enemy defeated callback after the WaveManager has set its callback
    this.setupEnemyDefeatedCallback();
    
    // Update Redux store
    store.dispatch(resetWaveState());
    store.dispatch(resetPlayerState());
  }
  
  /**
   * Sets up the enemy defeated callback
   */
  private setupEnemyDefeatedCallback(): void {
    this.enemyManager.setOnEnemyDefeated((enemy) => {
      // Award points based on enemy type
      const basePoints = this.scorePerEnemy;
      const enemyTypeMultiplier = this.getEnemyTypeScoreMultiplier(enemy.getType());
      const waveMultiplier = this.waveManager.getCurrentWaveNumber();
      
      const points = Math.floor(basePoints * enemyTypeMultiplier * (1 + (waveMultiplier - 1) * 0.1));
      
      this.addScore(points);
      
      // Play enemy defeat sound/effect
      // this.playSound('enemy_defeated');
    });
  }
  
  /**
   * Resets the game state
   */
  private resetGameState(): void {
    // Reset score
    this.currentScore = 0;
    
    // Clear enemies
    this.enemyManager.clearEnemies();
    
    // Reset player position
    if (this.playerController) {
      this.playerController.setPosition(new THREE.Vector3(0, 2, 0));
    }
  }
  
  /**
   * Pauses the game
   */
  public pauseGame(): void {
    if (!this.isGameActive) return;
    
    console.log('Game paused');
    
    // Set game state to paused
    store.dispatch(setGameState(GameStateEnum.PAUSED));
  }
  
  /**
   * Resumes the game
   */
  public resumeGame(): void {
    if (!this.isGameActive) return;
    
    console.log('Game resumed');
    
    // Set game state to wave in progress
    store.dispatch(setGameState(GameStateEnum.WAVE_IN_PROGRESS));
  }
  
  /**
   * Ends the game
   * @param victory Whether the player won
   */
  public endGame(victory: boolean = false): void {
    if (!this.isGameActive) return;
    
    console.log(`Game ended (${victory ? 'Victory' : 'Defeat'})`);
    
    // Set game as inactive
    this.isGameActive = false;
    
    // Set game state
    if (victory) {
      store.dispatch(setGameState(GameStateEnum.VICTORY));
    } else {
      store.dispatch(setGameState(GameStateEnum.GAME_OVER));
    }
  }
  
  /**
   * Updates the game controller
   * @param deltaTime Time since last update in seconds
   */
  public update(deltaTime: number): void {
    if (!this.isGameActive) return;
    
    // Get player position
    let playerPosition = new THREE.Vector3(0, 0, 0);
    if (this.playerController) {
      playerPosition = this.playerController.getPosition();
    }
    
    // Update enemy manager
    this.enemyManager.update(deltaTime, playerPosition);
    
    // Update wave manager
    this.waveManager.update(deltaTime);
    
    // Check for game over conditions
    this.checkGameOverConditions();
  }
  
  /**
   * Checks for game over conditions
   */
  private checkGameOverConditions(): void {
    // Check player health
    // In a real implementation, this would check the player's health
    // and end the game if it reaches zero
    
    // For now, we'll just check if the player has fallen off the map
    if (this.playerController && this.playerController.getPosition().y < -10) {
      this.endGame(false);
    }
  }
  
  /**
   * Gets the enemy manager
   * @returns The enemy manager
   */
  public getEnemyManager(): EnemyManager {
    return this.enemyManager;
  }
  
  /**
   * Gets the wave manager
   * @returns The wave manager
   */
  public getWaveManager(): WaveManager {
    return this.waveManager;
  }
  
  /**
   * Checks if the game is active
   * @returns True if the game is active
   */
  public isActive(): boolean {
    return this.isGameActive;
  }
  
  /**
   * Gets the current score
   * @returns The current score
   */
  public getScore(): number {
    return this.currentScore;
  }
  
  /**
   * Disposes of resources
   */
  public dispose(): void {
    this.isGameActive = false;
    
    // Dispose of managers
    this.enemyManager.dispose();
    this.waveManager.dispose();
  }
  
  /**
   * Continues to the next wave
   * This is called when the player clicks the "Continue" button in the upgrade menu
   */
  public continueToNextWave(): void {
    // Set game state back to WAVE_IN_PROGRESS
    store.dispatch(setGameState(GameStateEnum.WAVE_IN_PROGRESS));
    
    // Start the next wave
    this.waveManager.startNextWave();
  }
} 